/**
 * 数字输入字段（P0-4 属性面板的基础件）
 *
 * 设计要点：
 *  · 本地文本态 + 合法即提交：直接受控于 store 会让输入中的 "1." 被吃掉，
 *    这里用本地 state 保住光标与中间态，store 值从外部变（gizmo 拖拽）时再同步回显；
 *  · 非法输入不提交也不清空——用户能看到自己写了什么，改回来即可，无错误弹窗打扰；
 *  · min/max 钳制在提交时做（钳回而非拒绝），与 store 的钳制策略同构。
 */
import { useEffect, useState } from 'react';

import { clampNum, parseNum, round3 } from '../features/director/inspector';

export interface NumberFieldProps {
  label: string;
  value: number;
  onCommit: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
}

export function NumberField({ label, value, onCommit, step = 0.1, min, max }: NumberFieldProps) {
  const [text, setText] = useState(String(round3(value)));

  // 外部值变化（gizmo 拖拽回写等）且与本地编辑态不冲突时同步回显
  useEffect(() => {
    setText((prev) => (parseNum(prev, NaN) === round3(value) ? prev : String(round3(value))));
  }, [value]);

  return (
    <label className="flex min-w-0 flex-1 items-center gap-1">
      <span className="w-4 shrink-0 text-uiSm text-ink-muted">{label}</span>
      <input
        type="number"
        step={step}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const n = parseNum(e.target.value, NaN);
          if (Number.isFinite(n)) {
            onCommit(clampNum(n, min ?? -Infinity, max ?? Infinity));
          }
        }}
        className="w-full min-w-0 rounded-control border border-surface-line bg-surface-base px-1.5 py-1 font-num text-uiSm text-ink-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
      />
    </label>
  );
}
